import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { SubBrandsService } from '../sub-brands.service';
import { BrandsService } from '../../brands.service';
import { SubBrand } from '../sub-brands.model';
import { Brand } from '../../brands.model';
import { CardModule } from 'primeng/card';
import { EntityDropdownPickerComponent } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { IftaLabelModule } from 'primeng/iftalabel';

@Component({
  selector: 'app-sub-brands-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    ToastModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  templateUrl: './sub-brands-form.component.html',
  providers: [MessageService],
})
export class SubBrandFormComponent implements OnInit {
  subBrandId: number | null = null;
  subBrandForm!: FormGroup;
  isEditMode = false;
  pageTitle = 'Create New Sub Brand';
  brandPickerAdapter!: EntityDropdownPickerAdapter<Brand>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private subBrandsService: SubBrandsService,
    private brandsService: BrandsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.brandPickerAdapter = this.brandsService.createBrandPickerAdapter();
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.subBrandId = +id;
        this.isEditMode = true;
        this.pageTitle = 'Edit Sub Brand';
        this.loadSubBrandData(this.subBrandId);
      }
    });
  }

  initForm(): void {
    this.subBrandForm = this.fb.group({
      subBrandName: ['', Validators.required],
      subBrandCode: [''],
      brand: [null, Validators.required],
    });
  }

  loadSubBrandData(id: number): void {
    this.subBrandsService.getSubBrandById(id).subscribe({
      next: (subBrand) => {
        this.subBrandForm.patchValue({
          ...subBrand,
          brand: subBrand.brand ?? null,
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load business unit data.',
        });
        console.error(err);
      },
    });
  }

  saveSubBrand(): void {
    if (this.subBrandForm.invalid) {
      this.subBrandForm.markAllAsTouched();
      return;
    }

    const subBrandData: SubBrand = {
      id: this.subBrandId ?? 0,
      subBrandName: this.subBrandForm.value.subBrandName,
      subBrandCode: this.subBrandForm.value.subBrandCode,
      brand: this.subBrandForm.value.brand,
    };
    const operation = this.isEditMode
      ? this.subBrandsService.updateSubBrand(subBrandData)
      : this.subBrandsService.createSubBrand(subBrandData);

    operation.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Sub Brand ${
            this.isEditMode ? 'updated' : 'created'
          } successfully.`,
        });
        setTimeout(
          () =>
            this.router.navigate([
              '/ai-studio/session/current',
              { outlets: { canvasOutlet: ['sub-brand', 'list'] } },
            ]),
          1500
        );
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${
            this.isEditMode ? 'update' : 'create'
          } Sub Brand.`,
        });
        console.error(err);
      },
    });
  }

  cancel(): void {
    this.router.navigate([
      '/ai-studio/session/current',
      { outlets: { canvasOutlet: ['sub-brand', 'list'] } },
    ]);
  }
}
